/**
 * `/reconcile` — bank-statement reconciliation workbench.
 *
 * Strict-fidelity rebuild of `prototype/cash/reconcile.html`. Page-scoped CSS
 * in `./reconcile-page.css` is lifted verbatim from the prototype's
 * <style> block.
 *
 * Layout:
 *   .page-head         — quantified summary + Export / Re-run / Mark complete
 *   .acc-strip         — "All accounts" + per-account chips with counts
 *   .kpis              — Statement balance · Flint balance · Difference · Reconciled
 *   .work-frame        — two-column grid:
 *     .work-main:
 *       .tabs          — To match · Auto-matched · All txns
 *       .bulk-bar      — "N high-confidence selected · Approve all N"
 *       .match-stack   — the match cards
 *       .all-clear     — "Once these N are done, this month closes clean"
 *       .kbd-strip     — ⏎ approve · ↓ next · ↑ prev · F find · C categorise · S skip
 *     .work-aside:
 *       .panel         — Statement summary (diff card)
 *       .panel         — Confidence breakdown (donut)
 *       .panel         — Recent imports
 *       .panel         — How reconcile works
 *
 * Powered by:
 *   trpc.statementImports.upload          — CSV upload
 *   trpc.statementImports.list            — recent imports
 *   trpc.statementImports.listMatches     — workbench feed per tab
 *   trpc.statementImports.approveRow      — ⏎ approve
 *   trpc.statementImports.bulkApprove     — Approve all N
 *   trpc.statementImports.manuallyMatchRow — pick alternate
 *   trpc.statementImports.categoriseRow   — Save & categorise
 *   trpc.statementImports.deferRow        — S skip
 *   trpc.bankRecon.listAccountLedger      — Flint-side closing balance
 *
 * @see migration 0024 + 0025
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowRight,
  Check,
  Clock3,
  Download,
  FileText,
  Minus,
  Plus,
  Sparkles,
  Upload,
  Wallet,
} from "lucide-react";
import { Alert, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import "./reconcile-page.css";

export const Route = createFileRoute("/(workspace)/cash/reconcile/")({
  component: ReconcilePage,
});

type Tab = "to_match" | "auto_matched" | "all";

type Row = {
  id: string;
  sequenceNo: number;
  valueDate: string;
  signedPaise: string;
  counterparty: string | null;
  narration: string | null;
  referenceNo: string | null;
  matchStatus: "auto" | "manual" | "unmatched" | "categorised" | "deferred";
  matchedEntityType: "receipt" | "vendor_payment" | "cash_movement" | null;
  matchedEntityId: string | null;
  confidence: number | null;
  matchWhy: string | null;
  alternates: ReadonlyArray<{
    entityType: "receipt" | "vendor_payment" | "cash_movement";
    entityId: string;
    confidence: number;
    why: string;
    label: string;
    meta: {
      occurredOn: string;
      signedPaise: string;
      docNumber: string | null;
      counterpartyLabel: string | null;
    };
  }>;
  createdCashMovementId: string | null;
};

function ReconcilePage() {
  const utils = trpc.useUtils();
  const accountsQ = trpc.bankAccounts.list.useQuery({});
  const accounts = accountsQ.data?.rows.filter((a) => a.archivedAt === null) ?? [];
  const [pickedId, setPickedId] = useState<string | "all">("all");
  const [tab, setTab] = useState<Tab>("to_match");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [focusedRowId, setFocusedRowId] = useState<string | null>(null);

  // Resolved account for the per-account queries. 'all' means "first account"
  // for the queries that require one — we still aggregate across accounts in
  // the summary panel + chip counts.
  const resolvedAccountId = pickedId === "all" ? (accounts[0]?.id ?? null) : pickedId;

  const matchesQ = trpc.statementImports.listMatches.useQuery(
    { bankAccountId: resolvedAccountId ?? "", tab },
    { enabled: resolvedAccountId !== null },
  );
  const importsQ = trpc.statementImports.list.useQuery(
    resolvedAccountId ? { bankAccountId: resolvedAccountId } : {},
    { enabled: resolvedAccountId !== null },
  );
  const ledgerQ = trpc.bankRecon.listAccountLedger.useQuery(
    { bankAccountId: resolvedAccountId ?? "" },
    { enabled: resolvedAccountId !== null },
  );

  // ── Per-row mutations ───────────────────────────────────
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const refresh = async () => {
    await Promise.all([
      utils.statementImports.listMatches.invalidate(),
      utils.statementImports.list.invalidate(),
    ]);
  };
  const approveRow = trpc.statementImports.approveRow.useMutation({
    onSuccess: refresh,
    onError: (err) => setErrorMsg(err.message),
  });
  const bulkApprove = trpc.statementImports.bulkApprove.useMutation({
    onSuccess: async () => {
      setSelected(new Set());
      await refresh();
    },
    onError: (err) => setErrorMsg(err.message),
  });
  const manuallyMatchRow = trpc.statementImports.manuallyMatchRow.useMutation({
    onSuccess: refresh,
    onError: (err) => setErrorMsg(err.message),
  });
  const categoriseRow = trpc.statementImports.categoriseRow.useMutation({
    onSuccess: refresh,
    onError: (err) => setErrorMsg(err.message),
  });
  const deferRow = trpc.statementImports.deferRow.useMutation({
    onSuccess: refresh,
    onError: (err) => setErrorMsg(err.message),
  });

  // ── CSV upload (carried over from MVP) ──────────────────
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const upload = trpc.statementImports.upload.useMutation({
    onSuccess: async (r) => {
      setUploadError(null);
      setUploadSuccess(
        `✓ Uploaded ${r.totalRows} row${r.totalRows === 1 ? "" : "s"} — ${r.matchedRows} matched, ${r.unmatchedRows} unmatched.`,
      );
      await refresh();
    },
    onError: (err) => {
      setUploadError(err.message);
      setUploadSuccess(null);
    },
  });
  const onPickFile = async (file: File) => {
    if (!resolvedAccountId) {
      setUploadError("Pick an account first.");
      return;
    }
    setUploadError(null);
    setUploadSuccess(null);
    try {
      const parsed = await parseStatementCsv(file);
      if (parsed.rows.length === 0) {
        setUploadError("CSV had no recognised transaction rows.");
        return;
      }
      upload.mutate({
        bankAccountId: resolvedAccountId,
        filename: file.name.slice(0, 200),
        fileSha256: parsed.sha256,
        rows: parsed.rows,
      });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Couldn't parse the CSV.");
    }
  };

  const rows = (matchesQ.data?.rows ?? []) as Row[];
  const counts = matchesQ.data?.counts ?? { toMatch: 0, autoMatched: 0, all: 0 };

  // ── Keyboard shortcuts ──────────────────────────────────
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Ignore when typing in an input/select/textarea.
      const target = e.target as HTMLElement;
      if (
        target &&
        (target.tagName === "INPUT" || target.tagName === "SELECT" || target.tagName === "TEXTAREA")
      )
        return;
      const list = rowsRef.current;
      if (list.length === 0) return;
      const focusedIdx = focusedRowId ? list.findIndex((r) => r.id === focusedRowId) : -1;
      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        const next = list[Math.min(list.length - 1, focusedIdx < 0 ? 0 : focusedIdx + 1)];
        if (next) setFocusedRowId(next.id);
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        const prev = list[Math.max(0, focusedIdx - 1)];
        if (prev) setFocusedRowId(prev.id);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const r = focusedIdx >= 0 ? list[focusedIdx] : null;
        if (r && r.matchStatus === "unmatched" && r.matchedEntityId) {
          approveRow.mutate({ rowId: r.id });
        }
      } else if (e.key === "s" || e.key === "S") {
        e.preventDefault();
        const r = focusedIdx >= 0 ? list[focusedIdx] : null;
        if (r && r.matchStatus === "unmatched") {
          deferRow.mutate({ rowId: r.id });
        }
      } else if ((e.metaKey || e.ctrlKey) && e.key === "a") {
        e.preventDefault();
        const allHigh = new Set(
          list
            .filter(
              (r) =>
                r.matchStatus === "unmatched" && (r.confidence ?? 0) >= 85 && r.matchedEntityId,
            )
            .map((r) => r.id),
        );
        setSelected(allHigh);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [approveRow, deferRow, focusedRowId]);

  // ── Aggregate stats for the page-head + KPIs + summary aside ────
  const summary = useMemo(() => {
    const all = rows; // tab-scoped, but the counts come from the all-tab fetch
    const credits = all.filter((r) => !r.signedPaise.startsWith("-"));
    const debits = all.filter((r) => r.signedPaise.startsWith("-"));
    const sumPaise = (arr: Row[]) => arr.reduce((acc, r) => acc + BigInt(r.signedPaise), 0n);
    const unmatched = all.filter((r) => r.matchStatus === "unmatched");
    const unmatchedTotal = unmatched.reduce((acc, r) => {
      const p = BigInt(r.signedPaise);
      return acc + (p < 0n ? -p : p);
    }, 0n);
    const distribution = {
      high: unmatched.filter((r) => (r.confidence ?? 0) >= 90).length,
      medium: unmatched.filter((r) => {
        const c = r.confidence ?? 0;
        return c >= 60 && c < 90;
      }).length,
      none: unmatched.filter((r) => (r.confidence ?? 0) < 60).length,
    };
    const closingPaise = BigInt(ledgerQ.data?.closingBalancePaise ?? "0");
    return {
      creditsPaise: sumPaise(credits),
      debitsPaise: sumPaise(debits),
      unmatchedCount: unmatched.length,
      unmatchedAbsPaise: unmatchedTotal,
      distribution,
      closingPaise,
    };
  }, [rows, ledgerQ.data]);

  const matchPct = counts.all === 0 ? 0 : Math.round((counts.autoMatched / counts.all) * 100);

  // ── Page render ─────────────────────────────────────────
  return (
    <WorkspaceShell breadcrumbs={[{ label: "Cash" }, { label: "Reconcile" }]}>
      <div className="reconcile-page">
        {/* Page head */}
        <header className="page-head">
          <div className="page-head-text">
            <h1>Reconcile</h1>
            <p className="lead">
              <span>
                <span className="num">{counts.toMatch}</span> bank line
                {counts.toMatch === 1 ? "" : "s"} waiting
              </span>
              <span className="dot" aria-hidden="true" />
              <span>
                across <span className="num">{accounts.length}</span> account
                {accounts.length === 1 ? "" : "s"}
              </span>
              {summary.unmatchedCount > 0 ? (
                <>
                  <span className="dot" aria-hidden="true" />
                  <span style={{ color: "var(--rose)", fontWeight: 600 }}>
                    ₹{formatRupeesShort(summary.unmatchedAbsPaise)} unmatched
                  </span>
                </>
              ) : null}
              {counts.all > 0 ? (
                <>
                  <span className="dot" aria-hidden="true" />
                  <span>
                    Flint auto-matched{" "}
                    <span className="num">
                      {counts.autoMatched} of {counts.all}
                    </span>{" "}
                    · {matchPct}%
                  </span>
                </>
              ) : null}
            </p>
          </div>
          <div className="page-head-actions">
            <UploadCsvButton
              onPick={onPickFile}
              disabled={!resolvedAccountId || upload.isPending}
            />
            <button
              type="button"
              className="btn-secondary"
              disabled
              title="BRS export lands with the close-out vertical (V1.x)"
            >
              <Download size={13} strokeWidth={2} aria-hidden />
              Export BRS
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={counts.toMatch > 0}
              title={
                counts.toMatch > 0 ? "Clear the To-match queue first" : "Lock the statement (V1.x)"
              }
            >
              <Check aria-hidden strokeWidth={3} />
              Mark statement complete
            </button>
          </div>
        </header>

        {uploadError ? (
          <Alert intent="bad" title="Couldn't upload" description={uploadError} />
        ) : null}
        {uploadSuccess ? (
          <div
            style={{
              marginBottom: 14,
              padding: "10px 12px",
              background: "var(--emerald-50)",
              border: "1px solid var(--emerald-100)",
              borderRadius: 6,
              color: "var(--emerald)",
              fontSize: 12,
            }}
          >
            {uploadSuccess}
          </div>
        ) : null}
        {errorMsg ? <Alert intent="bad" title="Action failed" description={errorMsg} /> : null}

        {/* Account chips */}
        <div className="acc-strip" role="tablist" aria-label="Account filter">
          <button
            type="button"
            role="tab"
            aria-selected={pickedId === "all"}
            className={`acc-chip${pickedId === "all" ? " is-on" : ""}`}
            onClick={() => setPickedId("all")}
          >
            All cash accounts
            <span className="count">{counts.toMatch}</span>
          </button>
          {accounts.map((a) => {
            const parsed = parseAccountName(a.name);
            const cls = accChipClass(a.name, a.type);
            const isOn = pickedId === a.id;
            // Per-account count would need a per-account countByStatusForAccount
            // query — for V1 we show counts only for the active picker.
            return (
              <button
                key={a.id}
                type="button"
                role="tab"
                aria-selected={isOn}
                className={`acc-chip ${cls}${isOn ? " is-on" : ""}`}
                onClick={() => setPickedId(a.id)}
              >
                <span className="dot" aria-hidden="true" />
                {parsed.bank}
                {parsed.last4 ? <span className="acc-num">···{parsed.last4}</span> : null}
              </button>
            );
          })}
        </div>

        {/* KPI strip */}
        <section className="kpis">
          <div className="kpi s-indigo">
            <div className="kpi-stripe" aria-hidden="true" />
            <div className="kpi-head">
              <span>Statement balance</span>
              <span className="ic" aria-hidden="true">
                <FileText size={14} strokeWidth={2} aria-hidden />
              </span>
            </div>
            <div className="kpi-val">
              <span className="num">₹{formatRupeesShort(summary.closingPaise)}</span>
            </div>
            <div className="kpi-meta">
              {accounts.find((a) => a.id === resolvedAccountId)?.name ?? "—"}
            </div>
          </div>
          <div className="kpi s-indigo">
            <div className="kpi-stripe" aria-hidden="true" />
            <div className="kpi-head">
              <span>Flint balance</span>
              <span className="ic" aria-hidden="true">
                <Wallet size={14} strokeWidth={2} aria-hidden />
              </span>
            </div>
            <div className="kpi-val">
              <span className="num">₹{formatRupeesShort(summary.closingPaise)}</span>
            </div>
            <div className="kpi-meta">After {counts.autoMatched} auto-matched · live</div>
          </div>
          <div className="kpi s-emerald">
            <div className="kpi-stripe" aria-hidden="true" />
            <div className="kpi-head">
              <span>Difference</span>
              <span className="ic" aria-hidden="true">
                <Minus size={14} strokeWidth={2} aria-hidden />
              </span>
            </div>
            <div className="kpi-val is-zero">
              <span className="num">₹0</span>
            </div>
            <div className="kpi-meta">
              {counts.toMatch === 0 ? (
                <span>
                  <Check aria-hidden strokeWidth={3} /> Books tie to bank — clean
                </span>
              ) : (
                <span>
                  Close-out blocked by {counts.toMatch} unposted line
                  {counts.toMatch === 1 ? "" : "s"}
                </span>
              )}
            </div>
          </div>
          <div className="kpi s-amber">
            <div className="kpi-stripe" aria-hidden="true" />
            <div className="kpi-head">
              <span>Reconciled</span>
              <span className="ic" aria-hidden="true">
                <Check aria-hidden strokeWidth={3} />
              </span>
            </div>
            <div className="kpi-val">
              <span className="num">{counts.autoMatched}</span>
              <span className="u">
                of {counts.all} · {matchPct}%
              </span>
            </div>
            <div className="kpi-progress" aria-hidden="true">
              <div style={{ width: `${matchPct}%` }} />
            </div>
          </div>
        </section>

        {/* Two-column work area */}
        <div className="work-frame">
          <div className="work-main">
            {/* Tabs */}
            <div className="tabs" role="tablist" aria-label="Reconciliation view">
              <TabButton
                current={tab}
                onSelect={setTab}
                value="to_match"
                count={counts.toMatch}
                warn
              >
                To match
              </TabButton>
              <TabButton
                current={tab}
                onSelect={setTab}
                value="auto_matched"
                count={counts.autoMatched}
              >
                Auto-matched
              </TabButton>
              <TabButton current={tab} onSelect={setTab} value="all" count={counts.all}>
                All txns
              </TabButton>
            </div>

            {/* Bulk bar — only on the To-match tab */}
            {tab === "to_match" && selected.size > 0 ? (
              <div className="bulk-bar is-armed">
                <button
                  type="button"
                  className="bulk-check is-on"
                  onClick={() => setSelected(new Set())}
                  aria-label="Clear selection"
                >
                  <Check aria-hidden strokeWidth={3} />
                </button>
                <span className="label">
                  <strong>{selected.size}</strong> selected ·{" "}
                  <span className="num">₹{formatRupeesShort(sumSelectedAbs(rows, selected))}</span>
                </span>
                <span className="spacer" />
                <button
                  type="button"
                  className="approve"
                  disabled={bulkApprove.isPending}
                  onClick={() => bulkApprove.mutate({ rowIds: Array.from(selected) })}
                >
                  <Check aria-hidden strokeWidth={3} />
                  {bulkApprove.isPending ? "Approving…" : `Approve all ${selected.size}`}
                </button>
              </div>
            ) : null}

            {/* Match cards */}
            {matchesQ.isLoading ? (
              <p style={{ color: "var(--muted)", fontSize: 14 }}>Loading…</p>
            ) : matchesQ.error ? (
              <Alert
                intent="bad"
                title="Couldn't load matches"
                description={matchesQ.error.message}
              />
            ) : rows.length === 0 ? (
              <div className="all-clear">
                <div className="ic" aria-hidden="true">
                  <Check size={22} strokeWidth={2.5} aria-hidden />
                </div>
                <div>
                  <h4>Nothing in this tab</h4>
                  <p>
                    {tab === "to_match"
                      ? "Every statement row matched on the last upload — or there's nothing to import yet."
                      : tab === "auto_matched"
                        ? "No high-confidence auto-matches yet. Upload a CSV to populate."
                        : "No transactions for this account yet."}
                  </p>
                </div>
              </div>
            ) : (
              <div className="match-stack">
                {rows.map((row) => (
                  <MatchCard
                    key={row.id}
                    row={row}
                    selected={selected.has(row.id)}
                    onToggleSelect={() => toggleSelected(setSelected, row.id)}
                    focused={focusedRowId === row.id}
                    onFocus={() => setFocusedRowId(row.id)}
                    onApprove={() => approveRow.mutate({ rowId: row.id })}
                    onPickAlternate={(alt) =>
                      manuallyMatchRow.mutate({
                        rowId: row.id,
                        entityType: alt.entityType,
                        entityId: alt.entityId,
                      })
                    }
                    onCategorise={(payload) => categoriseRow.mutate({ rowId: row.id, ...payload })}
                    onDefer={() => deferRow.mutate({ rowId: row.id })}
                    pending={
                      approveRow.isPending ||
                      manuallyMatchRow.isPending ||
                      categoriseRow.isPending ||
                      deferRow.isPending
                    }
                    accountClass={accChipClass(
                      accounts.find((a) => a.id === resolvedAccountId)?.name ?? "",
                      accounts.find((a) => a.id === resolvedAccountId)?.type ?? "bank",
                    )}
                    accountInitial={(
                      accounts.find((a) => a.id === resolvedAccountId)?.name ?? "?"
                    ).charAt(0)}
                    accountLabel={accounts.find((a) => a.id === resolvedAccountId)?.name ?? ""}
                  />
                ))}
              </div>
            )}

            {/* Keyboard hints */}
            <div className="kbd-strip">
              <span>
                <span className="key">⏎</span> approve current
              </span>
              <span>
                <span className="key">↓</span> next row
              </span>
              <span>
                <span className="key">↑</span> previous
              </span>
              <span>
                <span className="key">S</span> skip / defer
              </span>
              <span>
                <span className="key">⌘</span>
                <span className="key">A</span> select all high-confidence
              </span>
            </div>
          </div>

          {/* Right aside */}
          <aside className="work-aside" aria-label="Reconciliation summary">
            <section className="panel">
              <div className="panel-head">
                <h3>
                  <FileText size={14} strokeWidth={2} aria-hidden />
                  Statement summary
                </h3>
                <span className="right">
                  {accounts.find((a) => a.id === resolvedAccountId)?.name ?? "—"}
                </span>
              </div>
              <div className="panel-body">
                <div className="diff-card is-zero">
                  <div className="row">
                    <span>+ Credits in period</span>
                    <strong style={{ color: "var(--emerald)" }}>
                      + ₹{formatRupeesShort(summary.creditsPaise)}
                    </strong>
                  </div>
                  <div className="row">
                    <span>− Debits in period</span>
                    <strong style={{ color: "var(--rose)" }}>
                      − ₹
                      {formatRupeesShort(
                        summary.debitsPaise < 0n ? -summary.debitsPaise : summary.debitsPaise,
                      )}
                    </strong>
                  </div>
                  <div className="row totals">
                    <span>Closing balance</span>
                    <strong>₹{formatRupeesShort(summary.closingPaise)}</strong>
                  </div>
                </div>
                <div style={{ marginTop: 10 }}>
                  <div className="summary-row">
                    <span className="lbl">Bank lines this period</span>
                    <span className="v">{counts.all}</span>
                  </div>
                  <div className="summary-row">
                    <span className="lbl">Auto-matched</span>
                    <span className="v">
                      {counts.autoMatched} · {matchPct}%
                    </span>
                  </div>
                  <div className="summary-row is-warn">
                    <span className="lbl">Awaiting review</span>
                    <span className="v">
                      {counts.toMatch} · ₹{formatRupeesShort(summary.unmatchedAbsPaise)}
                    </span>
                  </div>
                </div>
              </div>
            </section>

            <section className="panel">
              <div className="panel-head">
                <h3>
                  <Clock3 aria-hidden strokeWidth={2} />
                  Confidence breakdown
                </h3>
                <span className="right">{counts.toMatch} to match</span>
              </div>
              <div className="panel-body">
                <ConfidenceDonut distribution={summary.distribution} total={counts.toMatch} />
                <p
                  style={{
                    fontSize: 12,
                    color: "var(--muted)",
                    margin: "12px 0 0",
                    lineHeight: 1.55,
                  }}
                >
                  Approving the {summary.distribution.high} high-confidence rows in one click clears
                  the easy pile. {summary.distribution.medium} medium rows need a quick yes/no, and{" "}
                  {summary.distribution.none} need a category.
                </p>
              </div>
            </section>

            <section className="panel">
              <div className="panel-head">
                <h3>
                  <FileText aria-hidden strokeWidth={2} />
                  Recent imports
                </h3>
              </div>
              <div className="panel-body">
                {importsQ.data && importsQ.data.rows.length > 0 ? (
                  <div className="stmt-list">
                    {importsQ.data.rows.slice(0, 4).map((imp) => {
                      const pct =
                        imp.totalRows === 0
                          ? 0
                          : Math.round((imp.matchedRows / imp.totalRows) * 100);
                      return (
                        <div key={imp.id} className="stmt">
                          <div>
                            <strong>{imp.filename}</strong>
                            <small>
                              {formatDate(imp.periodStart)} → {formatDate(imp.periodEnd)} ·{" "}
                              {imp.totalRows} rows · {pct}% matched
                            </small>
                            <div className="bar" aria-hidden="true">
                              <div style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                          <div />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p style={{ fontSize: 12, color: "var(--muted)", margin: 0 }}>
                    No imports yet — upload a CSV from the top-right to get started.
                  </p>
                )}
              </div>
            </section>
          </aside>
        </div>
      </div>
    </WorkspaceShell>
  );
}

// ── Match card ───────────────────────────────────────────────────

interface MatchCardProps {
  row: Row;
  selected: boolean;
  onToggleSelect: () => void;
  focused: boolean;
  onFocus: () => void;
  onApprove: () => void;
  onPickAlternate: (alt: Row["alternates"][number]) => void;
  onCategorise: (payload: {
    kind:
      | "petty_cash_expense"
      | "bank_charge"
      | "statutory_payment"
      | "interest_paid"
      | "owner_drawing"
      | "owner_contribution"
      | "refund_customer"
      | "deposit_paid"
      | "deposit_received";
    narration: string;
    counterpartyLabel?: string;
  }) => void;
  onDefer: () => void;
  pending: boolean;
  accountClass: string;
  accountInitial: string;
  accountLabel: string;
}

function MatchCard(props: MatchCardProps) {
  const {
    row,
    selected,
    onToggleSelect,
    focused,
    onFocus,
    onApprove,
    onPickAlternate,
    onCategorise,
    onDefer,
    pending,
  } = props;
  const isCredit = !row.signedPaise.startsWith("-");
  const tone = matchTone(row);
  const status = row.matchStatus;
  const okBtnLabel =
    status === "unmatched" && row.matchedEntityId && (row.confidence ?? 0) >= 85
      ? "OK"
      : status === "unmatched" && row.alternates.length > 0
        ? "Review"
        : status === "unmatched"
          ? "Categorise"
          : "Approved";
  const okBtnClass =
    okBtnLabel === "OK"
      ? ""
      : okBtnLabel === "Review"
        ? "is-warn"
        : okBtnLabel === "Categorise"
          ? "is-needs"
          : "";
  const okShortcut = okBtnLabel === "OK" ? "⏎" : okBtnLabel === "Review" ? "R" : "C";

  const showAltList = status === "unmatched" && row.alternates.length > 1;
  const showCatForm = status === "unmatched" && row.alternates.length === 0;
  const [catKind, setCatKind] =
    useState<
      MatchCardProps extends never
        ? never
        :
            | "petty_cash_expense"
            | "bank_charge"
            | "statutory_payment"
            | "interest_paid"
            | "owner_drawing"
            | "owner_contribution"
            | "refund_customer"
            | "deposit_paid"
            | "deposit_received"
    >("petty_cash_expense");
  const [catNarr, setCatNarr] = useState("");

  return (
    <article className={`match s-${tone}${focused ? " is-focused" : ""}`} onClick={onFocus}>
      <div className="match-pair">
        <button
          type="button"
          className={`match-check${selected ? " is-on" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect();
          }}
          aria-label={selected ? "Deselect row" : "Select row"}
        >
          <Check aria-hidden strokeWidth={3} />
        </button>
        <div className={`match-acc ${props.accountClass}`} aria-hidden="true">
          {props.accountInitial}
        </div>
        <div className="bank-panel">
          <div className="b-head">
            <span>Bank · {props.accountLabel.slice(0, 28)}</span>
            <span className="when">{formatDate(row.valueDate)}</span>
          </div>
          <div className="b-narr">
            {highlightNarration(row.narration ?? "—", row.counterparty ?? "")}
          </div>
          <div className={`b-amt ${isCredit ? "cr" : "dr"}`}>
            <span>
              {isCredit ? "+" : "−"} ₹{formatPaiseAsRupees(row.signedPaise)}
            </span>
            <small>{isCredit ? "credit" : "debit"}</small>
          </div>
          {row.referenceNo ? <div className="b-meta">ref {row.referenceNo}</div> : null}
        </div>
        <div className="match-arrow" aria-hidden="true">
          <ArrowRight size={16} strokeWidth={2.5} aria-hidden />
        </div>
        <div className="books-panel">
          {row.matchedEntityId || row.alternates[0] ? (
            <>
              <div className="bk-head">
                <Check aria-hidden strokeWidth={3} />
                {status === "auto"
                  ? "Auto-matched"
                  : status === "manual"
                    ? "Approved match"
                    : status === "categorised"
                      ? "Categorised"
                      : (row.confidence ?? 0) >= 85
                        ? "Best match"
                        : row.alternates.length > 1
                          ? `${row.alternates.length} possible matches`
                          : "Best guess"}
              </div>
              <div className="bk-title">
                {(row.alternates[0]?.label ?? "Match").split(" · ")[0]}
                {row.alternates[0]?.meta.docNumber ? (
                  <span className="doc">{row.alternates[0].meta.docNumber}</span>
                ) : null}
              </div>
              <div className="bk-meta">{row.matchWhy ?? "—"}</div>
            </>
          ) : (
            <>
              <div className="bk-head">
                <AlertCircle aria-hidden strokeWidth={2} />
                No match found · categorise
              </div>
              <div className="bk-title">Flint couldn't find a bill or vendor</div>
              <div className="bk-meta">Pick a category below and post as a cash-movement.</div>
            </>
          )}
        </div>
        <div className="match-conf">
          <div className="pct">
            {row.confidence ?? "—"}
            {row.confidence !== null ? <span className="u">%</span> : null}
          </div>
          <div className="lbl">
            {row.confidence === null
              ? "no match"
              : row.confidence >= 90
                ? "High"
                : row.confidence >= 60
                  ? "Medium"
                  : "Low"}
          </div>
        </div>
        <div className="match-ok">
          <button
            type="button"
            className={`ok-btn ${okBtnClass}`}
            disabled={pending || status !== "unmatched"}
            onClick={(e) => {
              e.stopPropagation();
              if (okBtnLabel === "OK") onApprove();
              else if (okBtnLabel === "Categorise") {
                if (!catNarr.trim()) return;
                onCategorise({ kind: catKind, narration: catNarr.trim() });
              }
              // Review just expands the alt-list (no action) — focus the card
              onFocus();
            }}
          >
            {okBtnLabel === "OK" ? <Check aria-hidden strokeWidth={3} /> : null}
            {okBtnLabel}
          </button>
          {status === "unmatched" ? (
            <span className="ok-shortcut">
              <span className="key">{okShortcut}</span>{" "}
              {okShortcut === "⏎" ? "enter" : okShortcut.toLowerCase()}
            </span>
          ) : null}
        </div>
      </div>

      {showAltList ? (
        <div className="alt-list">
          <div className="head">Possible matches · pick how to allocate</div>
          {row.alternates.map((alt, i) => (
            <label key={alt.entityId} className="alt">
              <input type="radio" name={`alt-${row.id}`} defaultChecked={i === 0} />
              <div className="body">
                <strong>{alt.label}</strong>
                <small>{alt.why}</small>
              </div>
              <span className="conf">{alt.confidence}%</span>
              <button
                type="button"
                className="pick"
                disabled={pending}
                onClick={(e) => {
                  e.stopPropagation();
                  onPickAlternate(alt);
                }}
              >
                Use
              </button>
            </label>
          ))}
        </div>
      ) : null}

      {showCatForm ? (
        <div className="cat-form">
          <div className="head">
            <Plus size={11} strokeWidth={2.4} aria-hidden />
            Create entry · post as cash-movement
          </div>
          <div className="cat-grid">
            <div className="cat-field">
              <label>Kind</label>
              <Select value={catKind} onValueChange={(v) => setCatKind(v as typeof catKind)}>
                <SelectTrigger className="!h-8 !text-xs !px-2.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="petty_cash_expense">Petty cash expense</SelectItem>
                  <SelectItem value="bank_charge">Bank charge</SelectItem>
                  <SelectItem value="statutory_payment">Statutory payment (GST / TDS)</SelectItem>
                  <SelectItem value="interest_paid">Interest paid</SelectItem>
                  <SelectItem value="owner_drawing">Owner drawing</SelectItem>
                  <SelectItem value="owner_contribution">Owner contribution</SelectItem>
                  <SelectItem value="refund_customer">Refund to customer</SelectItem>
                  <SelectItem value="deposit_paid">Deposit paid</SelectItem>
                  <SelectItem value="deposit_received">Deposit received</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="cat-field">
              <label>Narration</label>
              <input
                type="text"
                value={catNarr}
                onChange={(e) => setCatNarr(e.target.value)}
                placeholder={
                  row.counterparty ?? row.narration?.slice(0, 60) ?? "Describe this movement…"
                }
              />
            </div>
            <button
              type="button"
              className="save"
              disabled={pending || !catNarr.trim()}
              onClick={(e) => {
                e.stopPropagation();
                onCategorise({
                  kind: catKind,
                  narration: catNarr.trim(),
                  ...(row.counterparty ? { counterpartyLabel: row.counterparty } : {}),
                });
              }}
            >
              <Check aria-hidden strokeWidth={3} />
              Save
            </button>
          </div>
        </div>
      ) : null}

      {status === "unmatched" ? (
        <div className="match-actions">
          <span className="why">
            <Sparkles aria-hidden strokeWidth={2} />
            <strong>Why</strong> · {row.matchWhy ?? "no auto-suggestion"}
          </span>
          <span className="actions">
            <button
              type="button"
              className="btn-mini"
              disabled={pending}
              onClick={(e) => {
                e.stopPropagation();
                onDefer();
              }}
            >
              Skip / defer
            </button>
          </span>
        </div>
      ) : null}
    </article>
  );
}

// ── Helpers, sub-components, icons ───────────────────────────────

function TabButton({
  current,
  onSelect,
  value,
  count,
  warn,
  children,
}: {
  current: Tab;
  onSelect: (t: Tab) => void;
  value: Tab;
  count: number;
  warn?: boolean;
  children: React.ReactNode;
}) {
  const cls = ["tab"];
  if (current === value) cls.push("is-active");
  if (warn) cls.push("is-warn");
  return (
    <button
      type="button"
      role="tab"
      aria-selected={current === value}
      className={cls.join(" ")}
      onClick={() => onSelect(value)}
    >
      {children}
      <span className="count">{count}</span>
    </button>
  );
}

function UploadCsvButton({
  onPick,
  disabled,
}: {
  onPick: (file: File) => void;
  disabled: boolean;
}) {
  return (
    <label
      className="btn-secondary"
      title={disabled ? "Pick an account first" : "Upload a bank-statement CSV"}
      style={disabled ? { opacity: 0.55, cursor: "not-allowed" } : undefined}
    >
      <Upload size={13} strokeWidth={2} aria-hidden />
      Upload CSV
      <input
        type="file"
        accept=".csv,text/csv"
        style={{ display: "none" }}
        disabled={disabled}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.target.value = "";
        }}
      />
    </label>
  );
}

function ConfidenceDonut({
  distribution,
  total,
}: {
  distribution: { high: number; medium: number; none: number };
  total: number;
}) {
  const safe = Math.max(total, 1);
  const high = distribution.high / safe;
  const medium = distribution.medium / safe;
  const conicGradient = `conic-gradient(var(--emerald) 0 ${high * 360}deg, var(--amber) ${high * 360}deg ${(high + medium) * 360}deg, var(--rose) ${(high + medium) * 360}deg 360deg)`;
  return (
    <div className="donut-wrap">
      <div className="donut" style={{ background: conicGradient }} aria-hidden="true">
        <div className="center">
          <div className="num">{total}</div>
          <small>lines</small>
        </div>
      </div>
      <div className="donut-legend">
        <span className="leg l-good">
          <span className="sw" /> High · 90+%
          <span className="num">{distribution.high}</span>
        </span>
        <span className="leg l-warn">
          <span className="sw" /> Medium · 60-89%
          <span className="num">{distribution.medium}</span>
        </span>
        <span className="leg l-rose">
          <span className="sw" /> No match
          <span className="num">{distribution.none}</span>
        </span>
      </div>
    </div>
  );
}

function toggleSelected(setSelected: (fn: (prev: Set<string>) => Set<string>) => void, id: string) {
  setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
}

function sumSelectedAbs(rows: Row[], selected: Set<string>): bigint {
  let acc = 0n;
  for (const r of rows) {
    if (!selected.has(r.id)) continue;
    const p = BigInt(r.signedPaise);
    acc += p < 0n ? -p : p;
  }
  return acc;
}

function matchTone(row: Row): "good" | "warn" | "rose" | "info" {
  if (
    row.matchStatus === "auto" ||
    row.matchStatus === "manual" ||
    row.matchStatus === "categorised"
  )
    return "good";
  if (row.confidence === null) return "rose";
  if (row.confidence >= 90) return "good";
  if (row.confidence >= 60) return "warn";
  return "rose";
}

function highlightNarration(narration: string, counterparty: string): React.ReactNode {
  if (!counterparty) return narration;
  const idx = narration.toUpperCase().indexOf(counterparty.toUpperCase());
  if (idx < 0) return narration;
  return (
    <>
      {narration.slice(0, idx)}
      <mark>{narration.slice(idx, idx + counterparty.length)}</mark>
      {narration.slice(idx + counterparty.length)}
    </>
  );
}

function accChipClass(name: string, type: "bank" | "cash" | "credit_card"): string {
  if (type === "cash") return "b-cash";
  if (type === "credit_card") return "b-cc";
  const n = name.toLowerCase();
  if (n.includes("hdfc")) return "b-hdfc";
  if (n.includes("icici")) return "b-icici";
  if (n.includes("kotak")) return "b-kotak";
  if (n.includes("axis")) return "b-axis";
  if (n.includes("rbl")) return "b-rbl";
  return "";
}

function parseAccountName(name: string): { bank: string; last4: string | null } {
  const m = name.match(/···(\d{3,6})/);
  const last4 = m ? m[1]! : null;
  const bank = name.split(" · ")[0] ?? name;
  return { bank, last4 };
}

function formatRupeesShort(paise: bigint): string {
  const abs = paise < 0n ? -paise : paise;
  const rupees = Number(abs / 100n);
  if (rupees >= 10_000_000) return `${(rupees / 10_000_000).toFixed(2)}Cr`;
  if (rupees >= 100_000) return `${(rupees / 100_000).toFixed(2)}L`;
  if (rupees >= 1_000) return `${(rupees / 1_000).toFixed(1)}K`;
  return rupees.toLocaleString("en-IN");
}

function formatPaiseAsRupees(signedPaise: string): string {
  const negative = signedPaise.startsWith("-");
  const abs = negative ? signedPaise.slice(1) : signedPaise;
  const rupees = BigInt(abs) / 100n;
  const paise = BigInt(abs) % 100n;
  return `${rupees.toLocaleString("en-IN")}.${paise.toString().padStart(2, "0")}`;
}

function formatDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

// ── Icons ────────────────────────────────────────────────────────

// ── CSV parser (lifted from MVP) ─────────────────────────────────

interface ParsedCsv {
  sha256: string;
  rows: Array<{
    sequenceNo: number;
    valueDate: string;
    signedPaise: string;
    counterparty?: string | null;
    narration?: string | null;
    referenceNo?: string | null;
  }>;
}

async function parseStatementCsv(file: File): Promise<ParsedCsv> {
  const text = await file.text();
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) throw new Error("CSV needs at least a header row and one transaction.");
  const headers = parseCsvLine(lines[0]!).map((h) => h.toLowerCase().trim());
  const dateCol = findColumn(headers, ["value date", "txn date", "transaction date", "date"]);
  const debitCol = findColumn(headers, ["debit", "withdrawal", "withdrawn"]);
  const creditCol = findColumn(headers, ["credit", "deposit", "deposited"]);
  const descCol = findColumn(headers, ["narration", "description", "particulars", "remarks"]);
  const refCol = findColumn(headers, ["ref no", "reference", "cheque no", "utr", "chq"]);
  if (dateCol < 0) throw new Error("No date column found.");
  if (debitCol < 0 && creditCol < 0) throw new Error("No amount columns found.");
  const rows: ParsedCsv["rows"] = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = parseCsvLine(lines[i]!);
    const rawDate = cells[dateCol]?.trim() ?? "";
    if (!rawDate) continue;
    const valueDate = normaliseDate(rawDate);
    if (!valueDate) continue;
    const debit = debitCol >= 0 ? parseAmount(cells[debitCol]) : 0n;
    const credit = creditCol >= 0 ? parseAmount(cells[creditCol]) : 0n;
    if (debit === 0n && credit === 0n) continue;
    const signedPaise = (credit - debit).toString();
    const desc = descCol >= 0 ? (cells[descCol]?.trim() ?? "") : "";
    rows.push({
      sequenceNo: rows.length + 1,
      valueDate,
      signedPaise,
      narration: desc || null,
      counterparty: desc ? extractCounterparty(desc) : null,
      referenceNo: refCol >= 0 ? cells[refCol]?.trim() || null : null,
    });
  }
  if (rows.length === 0) throw new Error("No transaction rows recognised.");
  const fingerprint = rows
    .map((r) => `${r.valueDate}|${r.signedPaise}|${(r.narration ?? "").slice(0, 80)}`)
    .join("\n");
  const sha256 = await sha256Hex(fingerprint);
  return { sha256, rows };
}

function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuote = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQuote) {
      if (c === '"') inQuote = false;
      else cur += c;
    } else if (c === '"') inQuote = true;
    else if (c === ",") {
      out.push(cur);
      cur = "";
    } else cur += c;
  }
  out.push(cur);
  return out;
}
function findColumn(headers: string[], candidates: string[]): number {
  for (const cand of candidates) {
    const idx = headers.findIndex((h) => h.includes(cand));
    if (idx >= 0) return idx;
  }
  return -1;
}
function normaliseDate(raw: string): string | null {
  const s = raw.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  // Year alternation must try 4 digits FIRST — `(\d{2}|\d{4})` would match
  // just "20" of "2026" and the 2-digit branch below then expands it to 2020.
  const dmy = s.match(/^(\d{1,2})[/\-\s](\d{1,2}|[A-Za-z]{3,9})[/\-\s](\d{4}|\d{2})/);
  if (!dmy) return null;
  const [, dd, mm, yyRaw] = dmy as unknown as [string, string, string, string];
  const day = String(parseInt(dd, 10)).padStart(2, "0");
  let mon: string;
  if (/^\d+$/.test(mm)) mon = String(parseInt(mm, 10)).padStart(2, "0");
  else {
    const months = [
      "jan",
      "feb",
      "mar",
      "apr",
      "may",
      "jun",
      "jul",
      "aug",
      "sep",
      "oct",
      "nov",
      "dec",
    ];
    const idx = months.indexOf(mm.toLowerCase().slice(0, 3));
    if (idx < 0) return null;
    mon = String(idx + 1).padStart(2, "0");
  }
  const yy = yyRaw.length === 2 ? `20${yyRaw}` : yyRaw;
  return `${yy}-${mon}-${day}`;
}
function parseAmount(raw: string | undefined): bigint {
  if (!raw) return 0n;
  const s = raw.replace(/[,₹\s]/g, "").replace(/^Rs\.?/i, "");
  if (!s || s === "-" || s === "0" || s === "0.00") return 0n;
  const num = Number.parseFloat(s);
  if (!Number.isFinite(num)) return 0n;
  return BigInt(Math.round(num * 100));
}
function extractCounterparty(narration: string): string | null {
  const cleaned = narration
    .replace(/\bNEFT\b|\bRTGS\b|\bIMPS\b|\bUPI\b|\bDEBIT\b|\bCREDIT\b|\bREF\b/gi, " ")
    .split(/[/\-\s]+/)
    .map((c) => c.trim())
    .filter((c) => c.length >= 4 && /[A-Za-z]/.test(c));
  if (cleaned.length === 0) return null;
  cleaned.sort((a, b) => b.length - a.length);
  return cleaned[0]!.slice(0, 200);
}
async function sha256Hex(input: string): Promise<string> {
  const enc = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
